import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      localStorage.removeItem('userData');
      return null;
    }
  });
  const [limits, setLimits] = useState(() => {
    try {
      const stored = localStorage.getItem('userLimits');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        clearAuth();
        return;
      }

      const response = await api.get('/auth/check');
      setUser(response.data.user);
      setLimits(response.data.limits);
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      localStorage.setItem('userLimits', JSON.stringify(response.data.limits));
    } catch {
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  const clearAuth = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userData');
    localStorage.removeItem('userLimits');
    setUser(null);
    setLimits(null);
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const data = response.data;
      setUser(data.user);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      // Загружаем лимиты после логина
      checkAuth();
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Ошибка при входе' };
    }
  };

  const loginWithTokens = (data) => {
    setUser(data.user);
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('userData', JSON.stringify(data.user));
    // Загружаем лимиты после OAuth логина
    checkAuth();
    return { success: true, user: data.user };
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    clearAuth();
  };

  const register = async (userData) => {
    try {
      await api.post('/auth/register', userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Ошибка регистрации' };
    }
  };

  const updateUser = (updates) => {
    setUser(prevUser => {
      const updated = { ...prevUser, ...updates };
      localStorage.setItem('userData', JSON.stringify(updated));
      return updated;
    });
  };

  const updateLimits = (newLimits) => {
    setLimits(newLimits);
    localStorage.setItem('userLimits', JSON.stringify(newLimits));
  };

  const refreshLimits = async () => {
    try {
      const response = await api.get('/auth/check');
      setLimits(response.data.limits);
      localStorage.setItem('userLimits', JSON.stringify(response.data.limits));
      return response.data.limits;
    } catch {
      return null;
    }
  };

  const value = { user, limits, loading, login, loginWithTokens, logout, register, checkAuth, updateUser, updateLimits, refreshLimits };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
